import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, BarChart3, TrendingDown, TrendingUp, Users } from 'lucide-react';
import { Badge, Card, CardTitle, SkeletonKpiCard } from '@/components/ui';
import { isApiError, unwrap } from '@/lib/api/envelope';
import { fetchDashboardSummary } from '@/features/clients/api';
import { api } from '@/lib/api/client';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';

interface CampaignStats {
  totalIdentified: number;
  identifiedToday: number;
  identifiedThisMonth: number;
}

async function fetchOpportunityStats(): Promise<CampaignStats> {
  const res = await api.get<ResponseEnvelope<CampaignStats>>(
    Endpoints.dashboard.adminOpportunityStats,
  );
  return unwrap(res);
}

export function ReportsPage() {
  const query = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: fetchDashboardSummary,
  });

  const oppQuery = useQuery({
    queryKey: ['dashboard', 'opportunity-stats'],
    queryFn: fetchOpportunityStats,
  });

  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-fg">Reports</h1>
          <p className="mt-0.5 text-sm text-fg-muted">Platform-wide statistics and client health overview.</p>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <SkeletonKpiCard key={i} />)}
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-xl font-semibold text-fg">Reports</h1>
        </div>
        <div role="alert" className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm dark:border-rose-500/25 dark:bg-rose-500/10">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" aria-hidden />
          <p className="text-rose-700 dark:text-rose-300">
            {isApiError(query.error) ? query.error.firstErrorMessage : 'Could not load report data.'}
          </p>
        </div>
      </div>
    );
  }

  const s = query.data;
  const successPct = s.todayVerifications > 0
    ? Math.round(s.todaySuccessRate * 100)
    : 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl font-semibold text-fg">Reports</h1>
        <p className="mt-0.5 text-sm text-fg-muted">
          Platform-wide statistics and client health overview.
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          icon={<Users className="h-4.5 w-4.5" />}
          label="Total clients"
          value={s.totalClients}
          sub={`${s.activeClients} active`}
          tone="primary"
        />
        <KpiCard
          icon={<BarChart3 className="h-4.5 w-4.5" />}
          label="Verifications today"
          value={s.todayVerifications}
          sub={`${successPct}% success rate`}
          tone="success"
        />
        <KpiCard
          icon={<TrendingDown className="h-4.5 w-4.5" />}
          label="Failed today"
          value={s.todayFailedVerifications}
          sub="No-match results"
          tone={s.todayFailedVerifications > 0 ? 'danger' : 'neutral'}
        />
        <KpiCard
          icon={<TrendingUp className="h-4.5 w-4.5" />}
          label="Low balance alerts"
          value={s.lowBalanceClientsCount}
          sub={`${s.expiringBalanceClientsCount} expiring soon`}
          tone={s.lowBalanceClientsCount > 0 ? 'warning' : 'neutral'}
        />
      </div>

      {/* Opportunity Identification card */}
      {oppQuery.data && (
        <Card elevated>
          <CardTitle className="mb-4">Opportunity Identification</CardTitle>
          <div className="grid grid-cols-3 divide-x divide-border">
            {[
              { label: 'Total identified', value: oppQuery.data.totalIdentified },
              { label: 'Identified today', value: oppQuery.data.identifiedToday },
              { label: 'This month', value: oppQuery.data.identifiedThisMonth },
            ].map(({ label, value }) => (
              <div key={label} className="px-4 text-center first:pl-0 last:pr-0">
                <p className="text-2xl font-bold tracking-tight text-fg">{value.toLocaleString()}</p>
                <p className="mt-0.5 text-xs text-fg-muted">{label}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Client status breakdown */}
      <Card>
        <CardTitle className="mb-4">Client status breakdown</CardTitle>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {[
            { label: 'Active', count: s.activeClients, tone: 'success' as const },
            { label: 'Onboarding', count: s.onboardingClients, tone: 'primary' as const },
            { label: 'Inactive', count: s.inactiveClients, tone: 'warning' as const },
            { label: 'Cancelled', count: s.cancelledClients, tone: 'neutral' as const },
            { label: 'Revoked', count: s.revokedClients, tone: 'danger' as const },
          ].map(({ label, count, tone }) => (
            <div
              key={label}
              className="flex flex-col items-center rounded-xl border border-border bg-surface-subtle/50 p-4 text-center"
            >
              <span className="text-2xl font-bold tracking-tight text-fg">{count}</span>
              <Badge tone={tone} className="mt-2">{label}</Badge>
            </div>
          ))}
        </div>
      </Card>

      {/* Low balance clients */}
      {s.lowBalanceClients.length > 0 && (
        <Card>
          <div className="mb-3 flex items-center gap-2">
            <CardTitle>Low balance clients</CardTitle>
            <span className="text-sm font-normal text-fg-muted">(below 100 verifications)</span>
          </div>
          <div className="space-y-2">
            {s.lowBalanceClients.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between rounded-lg border border-amber-200/60 bg-amber-50/50 px-3 py-2 text-sm dark:border-amber-500/20 dark:bg-amber-500/5"
              >
                <div>
                  <span className="font-medium text-fg">{c.clientName}</span>
                  <span className="ml-2 font-mono text-[10px] text-fg-subtle">{c.id}</span>
                </div>
                <Badge tone="warning">{(c.balance ?? 0).toLocaleString()} remaining</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Expiring balance clients */}
      {s.expiringBalanceClients.length > 0 && (
        <Card>
          <div className="mb-3 flex items-center gap-2">
            <CardTitle>Expiring balance</CardTitle>
            <span className="text-sm font-normal text-fg-muted">(within 30 days)</span>
          </div>
          <div className="space-y-2">
            {s.expiringBalanceClients.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm"
              >
                <div>
                  <span className="font-medium text-fg">{c.clientName}</span>
                  <span className="ml-2 font-mono text-[10px] text-fg-subtle">{c.id}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-fg-muted">
                    {c.balanceExpiryDate
                      ? new Date(c.balanceExpiryDate).toLocaleDateString()
                      : '—'}
                  </span>
                  <Badge tone="warning">{c.daysUntilBalanceExpiry}d left</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

type KpiTone = 'primary' | 'success' | 'warning' | 'danger' | 'neutral';

const kpiAccent: Record<KpiTone, { bar: string; icon: string }> = {
  primary: { bar: 'card-accent-primary', icon: 'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400' },
  success: { bar: 'card-accent-success', icon: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400' },
  warning: { bar: 'card-accent-warning', icon: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400' },
  danger:  { bar: 'card-accent-danger',  icon: 'bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400' },
  neutral: { bar: '',                    icon: 'bg-surface-subtle text-fg-muted' },
};

function KpiCard({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  sub: string;
  tone: KpiTone;
}) {
  const { bar, icon: iconCls } = kpiAccent[tone];
  return (
    <div className={`card-accent-top ${bar} relative overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-card dark:shadow-card-dark sm:p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-fg-muted">{label}</p>
          <p className="mt-2 text-2xl font-bold tracking-tight text-fg">{value.toLocaleString()}</p>
          <p className="mt-1 text-[11px] text-fg-subtle">{sub}</p>
        </div>
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconCls}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}
